import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Api as ApiIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Dashboard as DashboardIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getAllRoles } from "../../api/roles";
import { listUiComponents } from "../../api/uiPermissions";
import { getAllEndpointsStatus } from "../../api/apiManagement";
import { getAllUsers } from "../../api/userManagement";
import { getAllValues } from "../../api/codeValues";
import NavBar from "../NavBar";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    roles: 0,
    components: 0,
    endpoints: 0,
    users: 0,
    codeValues: 0,
    loading: true,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [
        rolesData,
        componentsData,
        endpointsData,
        usersData,
        codeValuesData,
      ] = await Promise.all([
        getAllRoles().catch(() => []),
        listUiComponents().catch(() => []),
        getAllEndpointsStatus().catch(() => ({})),
        getAllUsers({ size: 1 }).catch(() => ({ totalElements: 0 })),
        getAllValues().catch(() => ({ data: [] })),
      ]);

      setStats({
        roles: Array.isArray(rolesData) ? rolesData.length : 0,
        components: Array.isArray(componentsData) ? componentsData.length : 0,
        endpoints: Object.keys(endpointsData).length,
        users: usersData.totalElements || 0,
        codeValues: Array.isArray(codeValuesData.data)
          ? codeValuesData.data.length
          : 0,
        loading: false,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  const managementModules = [
    {
      title: "User Management",
      description: "Manage users, assign roles, and control user access",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#1976d2",
      path: "/admin/users",
      componentKey: "ADMIN_USERS_PAGE",
    },
    {
      title: "Role Management",
      description: "Create, view, and manage user roles and permissions",
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      color: "#9c27b0",
      path: "/admin/roles",
      componentKey: "ADMIN_ROLES_PAGE",
    },
    {
      title: "UI Permissions",
      description: "Configure which UI components are visible to which roles",
      icon: <VisibilityIcon sx={{ fontSize: 40 }} />,
      color: "#388e3c",
      path: "/admin/ui-permissions",
      componentKey: "ADMIN_UI_PERMISSIONS_PAGE",
    },
    {
      title: "API Management",
      description: "Enable/disable API endpoints and manage configurations",
      icon: <ApiIcon sx={{ fontSize: 40 }} />,
      color: "#f57c00",
      path: "/admin/api-management",
      componentKey: "ADMIN_API_MANAGEMENT_PAGE",
    },
    {
      title: "Code Values",
      description: "Manage dynamic code/value pairs and dropdown data",
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      color: "#9c27b0",
      path: "/admin/code-values",
      componentKey: "ADMIN_CODE_VALUES_PAGE",
    },
    {
      title: "Password Management",
      description: "Change passwords and manage password reset flows",
      icon: <LockIcon sx={{ fontSize: 40 }} />,
      color: "#d32f2f",
      path: "/admin/passwords",
      componentKey: "ADMIN_PASSWORDS_PAGE",
    },
  ];

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: color }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ModuleCard = ({ module }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        boxShadow: "0 4px 18px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
      onClick={() => navigate(module.path)}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Icon + Title Row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${module.color}33, ${module.color}66)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(6px)",
              boxShadow: `0 4px 10px ${module.color}33`,
            }}
          >
            {React.cloneElement(module.icon, {
              sx: { color: module.color, fontSize: 28 },
            })}
          </Box>

          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {module.title}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ mb: 3, minHeight: 40 }}
        >
          {module.description}
        </Typography>

        {/* Button */}
        <Button
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            background: `linear-gradient(90deg, ${module.color}, ${module.color}cc)`,
            boxShadow: `0 4px 12px ${module.color}55`,
            "&:hover": {
              background: `linear-gradient(90deg, ${module.color}dd, ${module.color})`,
            },
          }}
          fullWidth
        >
          Manage
        </Button>
      </CardContent>
    </Card>
  );

  if (stats.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant="h4"
            sx={{ fontSize: { xs: "22px", md: "23px" } }}
            component="h1"
            gutterBottom
          >
            {/* <DashboardIcon sx={{ mr: 2, verticalAlign: "middle" }} /> */}
            Admin Management Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage roles, permissions, API endpoints, and system configurations
          </Typography>
        </Box>

        {/* Stats Overview */}

        <Grid container spacing={4} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                borderLeft: "5px solid #1976d2", // accent border
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* Icon Section */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #42a5f5, #1976d2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                }}
              >
                <PeopleIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Total Users
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#0d47a1", fontWeight: 700 }}
                >
                  {stats.users}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                borderLeft: "5px solid #9c27b0", // purple accent
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* Icon Section */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #ba68c8, #9c27b0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 8px rgba(156, 39, 176, 0.3)",
                }}
              >
                <SecurityIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Total Roles
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#6a1b9a", fontWeight: 700 }}
                >
                  {stats.roles}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                borderLeft: "5px solid #388e3c", // purple accent
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* Icon Section */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #68c868ff, #388e3c)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 8px rgba(39, 176, 48, 0.3)",
                }}
              >
                <VisibilityIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  UI Components
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#388e3c", fontWeight: 700 }}
                >
                  {stats.components}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                borderLeft: "5px solid #f57c00", // purple accent
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* Icon Section */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #ee893cff, #f57c00)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 8px rgba(176, 121, 39, 0.3)",
                }}
              >
                <ApiIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  API Endpoints
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#f57c00", fontWeight: 700 }}
                >
                  {stats.endpoints}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                borderLeft: "5px solid #9c27b0", // Purple accent for Code Values
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* Icon Section */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #ce93d8, #9c27b0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 8px rgba(156, 39, 176, 0.3)",
                }}
              >
                <CodeIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Code Values
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#6a1b9a", fontWeight: 700 }}
                >
                  {stats.codeValues}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Management Modules */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
          Management Modules
        </Typography>

        <Grid container spacing={2}>
          {managementModules.map((module, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <ModuleCard module={module} />
            </Grid>
          ))}
        </Grid>

        {/* Quick Actions */}
        {/* <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate("/admin/users")}
              >
                View All Users
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<SecurityIcon />}
                onClick={() => navigate("/admin/roles")}
              >
                View All Roles
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate("/admin/ui-permissions")}
              >
                Configure UI Access
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<ApiIcon />}
                onClick={() => navigate("/admin/api-management")}
              >
                Manage APIs
              </Button>
            </Grid>
          </Grid>
        </Paper> */}

        {/* System Status */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>System Status:</strong> All management modules are
            operational. Use the navigation above to access specific management
            features.
          </Typography>
        </Alert>
      </Box>
    </>
  );
};

export default AdminDashboard;
